/**
 * Faithful port of `src/utils/brandColorSchemes.ts` — keep these files in lockstep.
 * The live preview must derive the same five Dawn schemes the zip generator stamps.
 *
 * Brand colors -> Shopify (Dawn) color schemes.
 *
 * A merchant gives one or two brand colors; a preset needs five populated
 * `color_schemes` entries in `config/settings_data.json`. Dawn's sections each
 * reference a scheme by id, so these five entries colour the whole storefront.
 *
 * Every text/background and label/button pair is checked against WCAG contrast
 * and the lighter side is pushed further until it passes. That is the point of
 * the module: a merchant picking a pale yellow should still get readable
 * buttons rather than a storefront that looks broken.
 *
 * Pure and deterministic — no Shopify dependency — so the same code drives the
 * in-app live preview and the exported theme settings, and the preview cannot
 * lie about what the merchant will get.
 */

/** Dawn scheme slots. Matches `color_schemes.scheme-N.settings`. */
export interface ShopifyColorScheme {
  background: string;
  background_gradient: string;
  text: string;
  button: string;
  button_label: string;
  secondary_button_label: string;
  shadow: string;
}

export type ShopifyColorSchemeSet = Record<
  'scheme-1' | 'scheme-2' | 'scheme-3' | 'scheme-4' | 'scheme-5',
  ShopifyColorScheme
>;

export interface BrandColors {
  /** Primary brand color — buttons and accents. */
  primary: string;
  /** Optional secondary/accent. Derived from primary when omitted. */
  accent?: string;
  /** Optional neutral for page backgrounds. Derived from primary when omitted. */
  neutral?: string;
}

export interface ContrastReport {
  scheme: string;
  pair: string;
  ratio: number;
  /** True when the pair met the target without adjustment. */
  passedUnadjusted: boolean;
}

export interface DeriveColorSchemesResult {
  schemes: ShopifyColorSchemeSet;
  /** Every checked pair and its final ratio, for the preview UI and tests. */
  contrast: ContrastReport[];
}

/** WCAG AA for body text. */
export const WCAG_AA_NORMAL_TEXT = 4.5;
/** WCAG AA for large text and UI components. */
export const WCAG_AA_LARGE_TEXT = 3;

interface Rgb { r: number; g: number; b: number }
interface Hsl { h: number; s: number; l: number }

// ── Color primitives ──────────────────────────────────────────────────────

/** Parse `#rgb` / `#rrggbb` (with or without `#`). Throws on anything else. */
export function parseHex(hex: string): Rgb {
  const cleaned = String(hex || '').trim().replace(/^#/, '');

  const expanded =
    cleaned.length === 3
      ? cleaned.split('').map((c) => c + c).join('')
      : cleaned;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const toHexPair = (value: number): string =>
  clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');

export function formatHex({ r, g, b }: Rgb): string {
  return `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));

  let h: number;
  if (max === rn) h = ((gn - bn) / delta) % 6;
  else if (max === gn) h = (bn - rn) / delta + 2;
  else h = (rn - gn) / delta + 4;

  h *= 60;
  if (h < 0) h += 360;

  return { h, s, l };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const hue = ((h % 360) + 360) % 360;
  const sat = clamp(s, 0, 1);
  const lig = clamp(l, 0, 1);

  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lig - c / 2;

  let rgb: [number, number, number];
  if (hue < 60) rgb = [c, x, 0];
  else if (hue < 120) rgb = [x, c, 0];
  else if (hue < 180) rgb = [0, c, x];
  else if (hue < 240) rgb = [0, x, c];
  else if (hue < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  return {
    r: (rgb[0] + m) * 255,
    g: (rgb[1] + m) * 255,
    b: (rgb[2] + m) * 255,
  };
}

/** WCAG relative luminance. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number): number => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two colors, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(parseHex(a));
  const lb = relativeLuminance(parseHex(b));
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

const adjustLightness = (hex: string, delta: number): string =>
  formatHex(hslToRgb({ ...rgbToHsl(parseHex(hex)), l: clamp(rgbToHsl(parseHex(hex)).l + delta, 0, 1) }));

// ── Contrast enforcement ──────────────────────────────────────────────────

/**
 * Nudge `foreground` away from `background` in lightness until the pair clears
 * `target`, walking in whichever direction has more headroom.
 *
 * Falls back to pure black or white when even a full walk cannot reach the
 * target — some background colors (a mid-tone saturated hue) simply have no
 * same-hue foreground that passes AA, and a readable fallback beats a
 * technically-on-brand unreadable one.
 */
export function ensureContrast(
  foreground: string,
  background: string,
  target: number = WCAG_AA_NORMAL_TEXT,
): string {
  if (contrastRatio(foreground, background) >= target) return foreground;

  const backgroundLuminance = relativeLuminance(parseHex(background));
  // Dark background -> lighten the foreground; light background -> darken it.
  const direction = backgroundLuminance < 0.5 ? 1 : -1;

  let candidate = foreground;
  for (let step = 0; step < 100; step += 1) {
    candidate = adjustLightness(candidate, direction * 0.01);
    if (contrastRatio(candidate, background) >= target) return candidate;
  }

  const white = '#ffffff';
  const black = '#000000';
  const whiteRatio = contrastRatio(white, background);
  const blackRatio = contrastRatio(black, background);

  if (Math.max(whiteRatio, blackRatio) < target) {
    // Nothing clears the target against this background. Return the better of
    // the two extremes rather than silently keeping an unreadable color.
    return whiteRatio >= blackRatio ? white : black;
  }

  return whiteRatio >= blackRatio ? white : black;
}

// ── Scheme derivation ─────────────────────────────────────────────────────

const NEAR_WHITE = '#ffffff';
const NEAR_BLACK = '#121212';

/**
 * Whichever of near-black / near-white reads best on `background`.
 *
 * Picking the better extreme up front means contrast enforcement only ever has
 * to nudge, never drag a wrong-way seed across the middle of the range — which
 * is where the muddy mid-greys come from.
 */
function preferredForegroundOn(background: string): string {
  return contrastRatio(NEAR_BLACK, background) >= contrastRatio(NEAR_WHITE, background)
    ? NEAR_BLACK
    : NEAR_WHITE;
}

/**
 * Clamp a merchant's brand color into a range that can actually anchor a
 * storefront. A near-white or near-black "brand color" produces buttons that
 * disappear, so pull extremes back toward usable territory while keeping hue.
 */
function normalizeBrandColor(hex: string): string {
  const hsl = rgbToHsl(parseHex(hex));
  const lightness = clamp(hsl.l, 0.18, 0.72);
  // A fully desaturated brand color is legitimate (monochrome brands), so
  // saturation is left alone.
  return formatHex(hslToRgb({ ...hsl, l: lightness }));
}

/**
 * Derive the five Dawn color schemes from a merchant's brand colors.
 *
 * The set is deliberately varied rather than five shades of one thing:
 *   scheme-1  white page, dark text, brand buttons      (default body)
 *   scheme-2  soft tinted surface                       (cards, alt sections)
 *   scheme-3  brand-colored surface, inverted text      (hero, callouts)
 *   scheme-4  near-black surface                        (footer, dramatic)
 *   scheme-5  accent-colored surface                    (sale, highlights)
 */
export function deriveColorSchemes(brand: BrandColors): DeriveColorSchemesResult {
  const primary = normalizeBrandColor(brand.primary);

  // Complementary-ish accent when none is supplied: rotate hue rather than
  // reuse the primary, so scheme-5 is visibly distinct.
  const primaryHsl = rgbToHsl(parseHex(primary));
  const accent = brand.accent
    ? normalizeBrandColor(brand.accent)
    : formatHex(hslToRgb({ ...primaryHsl, h: primaryHsl.h + 150 }));

  // Neutral surface: a very light tint of the brand hue, so "white" areas still
  // feel like the brand rather than default Shopify grey.
  const neutral = brand.neutral
    ? normalizeBrandColor(brand.neutral)
    : formatHex(hslToRgb({ h: primaryHsl.h, s: Math.min(primaryHsl.s, 0.18), l: 0.96 }));

  const contrast: ContrastReport[] = [];

  const build = (id: string, background: string, buttonSeed: string): ShopifyColorScheme => {
    // Seed text from whichever extreme already reads best on this background.
    // Seeding from a fixed colour instead lets `ensureContrast` walk a wrong-way
    // seed only as far as the first passing value — grey on yellow at 4.6:1,
    // which clears AA and still looks broken.
    const textSeed = preferredForegroundOn(background);
    const text = ensureContrast(textSeed, background, WCAG_AA_NORMAL_TEXT);
    const button = ensureContrast(buttonSeed, background, WCAG_AA_LARGE_TEXT);
    const buttonLabelSeed = preferredForegroundOn(button);
    const buttonLabel = ensureContrast(buttonLabelSeed, button, WCAG_AA_NORMAL_TEXT);

    const record = (pair: string, fg: string, bg: string, seed: string, target: number) => {
      contrast.push({
        scheme: id,
        pair,
        ratio: contrastRatio(fg, bg),
        passedUnadjusted: contrastRatio(seed, bg) >= target,
      });
    };

    record('text/background', text, background, textSeed, WCAG_AA_NORMAL_TEXT);
    record('button/background', button, background, buttonSeed, WCAG_AA_LARGE_TEXT);
    record('button_label/button', buttonLabel, button, buttonLabelSeed, WCAG_AA_NORMAL_TEXT);

    return {
      background,
      // Dawn treats an empty gradient as "no gradient", which is what presets want.
      background_gradient: '',
      text,
      button,
      button_label: buttonLabel,
      // Secondary buttons are text-on-background, so they follow the text color.
      secondary_button_label: text,
      shadow: NEAR_BLACK,
    };
  };

  const schemes: ShopifyColorSchemeSet = {
    'scheme-1': build('scheme-1', NEAR_WHITE, primary),
    'scheme-2': build('scheme-2', neutral, primary),
    // Brand-colored surfaces invert: the button contrasts against the brand.
    'scheme-3': build('scheme-3', primary, preferredForegroundOn(primary)),
    'scheme-4': build('scheme-4', NEAR_BLACK, primary),
    'scheme-5': build('scheme-5', accent, preferredForegroundOn(accent)),
  };

  return { schemes, contrast };
}

/**
 * Shape the derived schemes the way `config/settings_data.json` stores them, so
 * a preset bundle can merge this straight in.
 */
export function toSettingsDataColorSchemes(
  set: ShopifyColorSchemeSet,
): Record<string, { settings: ShopifyColorScheme }> {
  const out: Record<string, { settings: ShopifyColorScheme }> = {};
  for (const [id, settings] of Object.entries(set)) {
    out[id] = { settings };
  }
  return out;
}
